import { Card, CardContent, CardHeader, CardTitle } from '@agentselly/ui';
import { Download, FileText, Image } from 'lucide-react';
import { useTranslate } from '@tolgee/react';

interface Document {
  id: string;
  type: string;
  title: string;
  filename: string;
  url: string;
  status: 'uploaded' | 'missing' | 'processing';
  uploadedAt: Date;
  thumbnail?: string;
  size?: number;
  mimeType?: string;
  isPrimary?: boolean;
}

// Document type translations
const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  expose: 'Exposé',
  flyer: 'Flyer',
  seller_contract: 'Verkaufsvertrag',
  land_register: 'Grundbuchauszug',
  floor_plan: 'Grundrissplan',
  energy_certificate: 'Energieausweis',
  photo: 'Foto',
  contract: 'Vertrag',
  certificate: 'Zertifikat',
  plan: 'Plan',
  document: 'Dokument',
};

interface CockpitDocumentsWidgetProps {
  title: string;
  documents: Document[];
}

interface StaticDownloadLink {
  id: string;
  title: string;
  url: string;
}

// Besichtigungsmappe — static, non-Property download links (not sourced from
// Property.documents/assets). Add the second link as ONE line below once
// Renato provides the URL (DEV-1756); do not invent a URL in the meantime.
const BESICHTIGUNGSMAPPE_LINKS: StaticDownloadLink[] = [
  {
    id: 'besichtigungsmappe-gutschein',
    title: 'Gutschein Lila Set',
    url: 'https://26776524.fs1.hubspotusercontent-eu1.net/hubfs/26776524/Besichtigungsmappe/Gutschein-Agent-Selly.pdf',
  },
  {
    id: 'besichtigungsmappe-naechste-schritte',
    title: 'Immobilienkauf – nächste Schritte',
    url: 'https://26776524.fs1.hubspotusercontent-eu1.net/hubfs/26776524/Besichtigungsmappe/Immobilienkauf_n%C3%A4chste%20Schritte_V3.pdf',
  },
];

const SECTION_HEADER_CLASS =
  'flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2';

export function CockpitDocumentsWidget(props: CockpitDocumentsWidgetProps) {
  const { title, documents } = props;
  const { t } = useTranslate();

  // Separate documents and photos
  const primaryDocs = documents.filter((d) => d.isPrimary);
  const photoDocs = documents.filter((d) => d.type === 'photo' && !d.isPrimary);
  const otherDocs = documents.filter(
    (d) => !d.isPrimary && d.type !== 'photo' && d.type !== 'weitere_dokumente',
  );

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    const k = 1024;
    const sizes = [
      'Bytes',
      'KB',
      'MB',
      'GB',
    ];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const hasAnyDocuments =
    primaryDocs.length > 0 || otherDocs.length > 0 || photoDocs.length > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t(title)}</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {!hasAnyDocuments && BESICHTIGUNGSMAPPE_LINKS.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <FileText className="mx-auto h-12 w-12 mb-2 text-gray-400 dark:text-gray-500" />
            <p>Keine Dokumente vorhanden</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Primary Documents (Exposé & Flyer) */}
            {primaryDocs.length > 0 && (
              <div className="space-y-1.5">
                {primaryDocs.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between gap-3 p-3 border-2 border-primary-200 bg-primary-50 rounded-lg hover:bg-primary-100 focus-within:ring-2 focus-within:ring-primary-500 focus-within:ring-offset-2 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="h-5 w-5 text-primary-600 shrink-0" />
                      <div className="min-w-0">
                        <p className="font-semibold text-sm truncate">
                          {DOCUMENT_TYPE_LABELS[doc.type] || doc.title}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                          {doc.filename}
                          {doc.size ? ` · ${formatFileSize(doc.size)}` : ''}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => window.open(doc.url, '_blank')}
                      className="cockpit-doc-download-btn inline-flex items-center gap-2 shrink-0 px-4 py-2.5 rounded-lg font-medium text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 transition-colors"
                    >
                      <Download className="h-4 w-4" />
                      {t(
                        'cockpit_documents_dashboard_widget_download',
                        'Herunterladen',
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Other Documents */}
            {otherDocs.length > 0 && (
              <div className="space-y-1.5">
                {primaryDocs.length > 0 && (
                  <h3 className={SECTION_HEADER_CLASS}>Weitere Dokumente</h3>
                )}
                {otherDocs.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between gap-3 p-2.5 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 focus-within:ring-2 focus-within:ring-primary-500 focus-within:ring-offset-2 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="h-4 w-4 text-gray-400 dark:text-gray-500 shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">
                          {DOCUMENT_TYPE_LABELS[doc.type] || doc.title}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {doc.filename}
                          {doc.size ? ` · ${formatFileSize(doc.size)}` : ''}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => window.open(doc.url, '_blank')}
                      className="cockpit-doc-download-btn inline-flex items-center gap-2 shrink-0 px-4 py-2.5 rounded-lg font-medium text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 transition-colors"
                    >
                      <Download className="h-4 w-4" />
                      {t(
                        'cockpit_documents_dashboard_widget_download',
                        'Herunterladen',
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Besichtigungsmappe — static download links (DEV-1756) */}
            {BESICHTIGUNGSMAPPE_LINKS.length > 0 && (
              <div className="space-y-1.5">
                <h3 className={SECTION_HEADER_CLASS}>
                  {t(
                    'cockpit_documents_besichtigungsmappe_title',
                    'Besichtigungsmappe',
                  )}
                </h3>
                {BESICHTIGUNGSMAPPE_LINKS.map((link) => (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    className="flex items-center justify-between gap-3 p-2.5 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="h-4 w-4 text-gray-400 dark:text-gray-500 shrink-0" />
                      <p className="font-medium text-sm truncate">
                        {link.title}
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-2 shrink-0 px-4 py-2.5 rounded-lg font-medium text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600">
                      <Download className="h-4 w-4" />
                      {t(
                        'cockpit_documents_dashboard_widget_download',
                        'Herunterladen',
                      )}
                    </span>
                  </a>
                ))}
              </div>
            )}

            {/* Photo Gallery */}
            {photoDocs.length > 0 && (
              <div className="space-y-1.5">
                <h3 className={SECTION_HEADER_CLASS}>Fotos</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {photoDocs.map((photo) => (
                    <a
                      key={photo.id}
                      href={photo.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group relative aspect-square rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 hover:border-primary-400 transition-all hover:shadow-md dark:hover:shadow-gray-900/50 bg-gray-100 dark:bg-gray-800"
                    >
                      <img
                        src={photo.url}
                        alt={photo.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling?.classList.remove(
                            'hidden',
                          );
                        }}
                      />
                      <div className="hidden w-full h-full flex-col items-center justify-center bg-gray-100 dark:bg-gray-800">
                        <Image className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {photo.title}
                        </p>
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                      <div className="absolute bottom-0 left-0 right-0 p-2 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                        <p className="text-xs font-medium truncate">
                          {photo.title}
                        </p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
